import React, { useState } from 'react';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  MenuItem,
  Alert,
  CircularProgress,
  Grid,
  FormControl,
  InputLabel,
  Select,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@mui/material';
import { requestService } from '../services/api';

const propertyTypes = [
  { value: 'terrain', label: 'Terrain' },
  { value: 'maison', label: 'Maison' },
  { value: 'appartement', label: 'Appartement' },
  { value: 'bureau', label: 'Bureau/Commerce' },
  { value: 'voiture', label: 'Véhicule' },
  { value: 'autre', label: 'Autre bien' }
];

const countries = [
  { value: 'tg', label: 'Togo' },
  { value: 'bj', label: 'Bénin' },
  { value: 'ci', label: "Côte d'Ivoire" },
  { value: 'fr', label: 'France' },
  { value: 'de', label: 'Allemagne' },
  { value: 'us', label: 'États-Unis' },
  { value: 'autre', label: 'Autre pays' }
];

const PropertyForm = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    serviceType: 'immobilier',
    propertyType: '',
    operationType: 'achat',
    location: '',
    budget: '',
    currency: 'XOF',
    clientLocation: 'tg',
    description: '',
    propertyDetails: ''
  });
  
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      await requestService.submitRequest(formData);
      setSuccess(true);
      setFormData({
        fullName: '',
        email: '',
        phone: '',
        serviceType: 'immobilier',
        propertyType: '',
        operationType: 'achat',
        location: '',
        budget: '',
        currency: 'XOF',
        clientLocation: 'tg',
        description: '',
        propertyDetails: ''
      });
      
      setTimeout(() => {
        setSuccess(false);
      }, 5000);
    } catch (err) {
      setError(err.response?.data?.message || 'Une erreur est survenue');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="md">
      <Paper elevation={3} sx={{ p: 4, mt: 4, mb: 4 }}>
        <Typography variant="h4" gutterBottom color="primary" align="center">
          Immobilier & Gestion d'Actifs
        </Typography>
        
        <Typography variant="body1" paragraph align="center" color="text.secondary" sx={{ mb: 4 }}>
          Que vous soyez au Togo ou à l'étranger, nous vous accompagnons dans toutes
          vos transactions immobilières et de gestion d'actifs.
        </Typography>

        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            ✅ Votre demande a été enregistrée. Notre équipe immobilier vous contactera rapidement.
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            ❌ {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit} noValidate>
          <Grid container spacing={3}>
            {/* Informations personnelles */}
            <Grid size={{ xs: 12 }}>
              <Typography variant="h6" gutterBottom color="primary">
                Informations personnelles
              </Typography>
            </Grid>
            
            <Grid size={{ xs: 12, sm: 6}}>
              <TextField
                required
                fullWidth
                label="Nom complet"
                name="fullName"
                value={formData.fullName}
                onChange={handleChange}
                variant="outlined"
              />
            </Grid>
            
            <Grid size={{ xs: 12,sm: 6 }}>
              <TextField
                required
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                variant="outlined"
              />
            </Grid>
            
            <Grid size={{ xs: 12, sm: 6}}>
              <TextField
                required
                fullWidth
                label="Téléphone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                variant="outlined"
              />
            </Grid>
            
            <Grid size={{ xs: 12, sm: 6}}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Votre pays de résidence</InputLabel>
                <Select
                  name="clientLocation"
                  value={formData.clientLocation}
                  onChange={handleChange}
                  label="Votre pays de résidence"
                  required
                >
                  {countries.map((country) => (
                    <MenuItem key={country.value} value={country.value}>
                      {country.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Type d'opération */}
            <Grid size={{ xs: 12 }}>
              <Typography variant="h6" gutterBottom color="primary">
                Type d'opération
              </Typography>
              <FormControl component="fieldset">
                <RadioGroup
                  row
                  name="operationType"
                  value={formData.operationType}
                  onChange={handleChange}
                >
                  <FormControlLabel value="achat" control={<Radio />} label="Achat" />
                  <FormControlLabel value="vente" control={<Radio />} label="Vente" />
                  <FormControlLabel value="location" control={<Radio />} label="Location" />
                  <FormControlLabel value="gestion" control={<Radio />} label="Gestion" />
                </RadioGroup>
              </FormControl>
            </Grid>

            {/* Détails du bien */}
            <Grid size={{ xs: 12 ,sm: 6}}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Type de bien</InputLabel>
                <Select
                  name="propertyType"
                  value={formData.propertyType}
                  onChange={handleChange}
                  label="Type de bien"
                  required
                >
                  {propertyTypes.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid size={{ xs: 12,sm: 6}} >
              <TextField
                fullWidth
                label="Localisation souhaitée (ville, quartier)"
                name="location"
                value={formData.location}
                onChange={handleChange}
                variant="outlined"
                placeholder="Ex: Lomé, Tokoin"
              />
            </Grid>
            
            <Grid size={{ xs: 12, sm: 6}}>
              <TextField
                fullWidth
                label="Budget approximatif"
                name="budget"
                type="number"
                value={formData.budget}
                onChange={handleChange}
                variant="outlined"
                InputProps={{
                  endAdornment: (
                    <Typography variant="body2" color="text.secondary">
                      {formData.currency}
                    </Typography>
                  )
                }}
              />
            </Grid>
            
            <Grid size={{ xs: 12, sm: 6}}>
              <TextField
                fullWidth
                label="Caractéristiques spécifiques"
                name="propertyDetails"
                value={formData.propertyDetails}
                onChange={handleChange}
                variant="outlined"
                placeholder="Ex: 3 chambres, garage, etc."
              />
            </Grid>
            
            <Grid size={{ xs: 12 }}>
              <TextField
                required
                fullWidth
                multiline
                rows={4}
                label="Description de votre besoin"
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Décrivez en détail ce que vous recherchez ou proposez..."
                variant="outlined"
              />
            </Grid>
            
            <Grid size={{ xs: 12 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Notre service immobilier comprend: recherche de biens, vérification légale,
                négociation, gestion des formalités et accompagnement complet.
              </Typography>
            </Grid>
            
            <Grid size={{ xs: 12 }}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                size="large"
                fullWidth
                disabled={loading}
                sx={{ py: 1.5 }}
              >
                {loading ? <CircularProgress size={24} /> : 'Soumettre ma demande immobilière'}
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Paper>

      {/* Services offerts */}
      <Grid container spacing={2} sx={{ mb: 6 }}>
        <Grid size={{ xs: 12, sm: 6 ,md: 3}}>
          <Paper elevation={1} sx={{ p: 2, textAlign: 'center', height: '100%' }}>
            <Typography variant="subtitle1" fontWeight="bold" color="primary">
              🔍 Recherche
            </Typography>
            <Typography variant="body2">
              Trouver le bien idéal selon vos critères
            </Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, sm: 6 ,md: 3 }}>
          <Paper elevation={1} sx={{ p: 2, textAlign: 'center', height: '100%' }}>
            <Typography variant="subtitle1" fontWeight="bold" color="primary">
              ⚖️ Vérification
            </Typography>
            <Typography variant="body2">
              Vérification légale et foncière complète
            </Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12,sm: 6 ,md: 3 }}>
          <Paper elevation={1} sx={{ p: 2, textAlign: 'center', height: '100%' }}>
            <Typography variant="subtitle1" fontWeight="bold" color="primary">
              💼 Négociation
            </Typography>
            <Typography variant="body2">
              Négociation des meilleures conditions
            </Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, sm: 6 ,md: 3}}>
          <Paper elevation={1} sx={{ p: 2, textAlign: 'center', height: '100%' }}>
            <Typography variant="subtitle1" fontWeight="bold" color="primary">
              📑 Formalités
            </Typography>
            <Typography variant="body2">
              Gestion complète des documents légaux
            </Typography>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default PropertyForm;